import React, { useCallback, useState } from 'react';
import { LayoutChangeEvent } from 'react-native';

import { Stack } from '@/components/layout';
import { Carousel, type CarouselRenderItem } from '@/components/ui/carousel';
import { createStyles, useScaling } from '@/hooks';

import { LessonCard } from './card';
import {
  CARD_HEIGHT,
  CARD_HORIZONTAL_MARGIN,
  PARALLAX_ADJACENT_ITEM_SCALE,
  PARALLAX_PEEK_OFFSET,
} from './constants';
import { MOCK_GUARDIAN_LESSON_CAROUSEL_ITEMS } from './mock-data';
import { Pagination } from './pagination';
import type { GuardianLessonCarouselItem, GuardianLessonCarouselProps } from './types';

function LessonCarousel({
  items = MOCK_GUARDIAN_LESSON_CAROUSEL_ITEMS,
  onViewDetailsPress,
  onReschedulePress,
  onLocationPress,
  containerStyle,
}: GuardianLessonCarouselProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const [activeIndex, setActiveIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  }, []);

  const itemWidth = containerWidth > 0 ? containerWidth - scale(CARD_HORIZONTAL_MARGIN * 2) : 0;

  const renderItem = useCallback<CarouselRenderItem<GuardianLessonCarouselItem>>(
    ({ item }) => (
      <LessonCard
        item={item}
        width={itemWidth}
        onViewDetailsPress={onViewDetailsPress}
        onReschedulePress={onReschedulePress}
        onLocationPress={onLocationPress}
      />
    ),
    [itemWidth, onLocationPress, onReschedulePress, onViewDetailsPress],
  );

  return (
    <Stack align="center" style={[styles.root, containerStyle]} onLayout={handleLayout}>
      {containerWidth > 0 ? (
        <>
          <Carousel
            data={items}
            width={containerWidth}
            height={scale(CARD_HEIGHT)}
            renderItem={renderItem}
            showNav={false}
            loop={items.length > 1}
            pagingEnabled
            snapEnabled
            onSnapToItem={setActiveIndex}
            mode="parallax"
            modeConfig={{
              parallaxScrollingScale: 1,
              parallaxAdjacentItemScale: PARALLAX_ADJACENT_ITEM_SCALE,
              parallaxScrollingOffset: scale(PARALLAX_PEEK_OFFSET),
            }}
          />
          {items.length > 1 ? <Pagination count={items.length} activeIndex={activeIndex} /> : null}
        </>
      ) : null}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    width: '100%',
    paddingTop: scale(19),
  },
}));

export { MOCK_GUARDIAN_LESSON_CAROUSEL_ITEMS } from './mock-data';
export type { GuardianLessonCarouselItem, GuardianLessonCarouselProps } from './types';
export default LessonCarousel;
